import { useStore, useActions } from '../../store/hooks'
import { Stack, Flex, Text, Link, Icon, Skeleton, useBackendQuery, Grid } from '@thirstycamel/ui'
import { getStoreOpenCloseStatus } from '@ts/core/src/shared/services/time'
import { ProductAvailabilityProps } from '../ProductAvailability/ProductAvailability'
import StoreEntity from '@ts/core/src/modules/store/store.entity'
import StoreCardSlim from '../StoreCardSlim'
import range from 'lodash.range'
import { useCart } from '../../hooks/useCart'
import { ProductSourceOptions } from '../ProductSource'

export const ProductAvailabilityStore = ({ product, productSource }: ProductAvailabilityProps) => {
  const selectedStore = useStore(s => s.store.selectedStore)
  const { selectStore } = useActions(a => a.store)
  const { fetchProductLists } = useActions(actions => actions.navigation)

  const { data, isFetching } = useBackendQuery<StoreEntity[]>([
    `/products/${product?.id}/nearby-availability`,
    { params: { store: selectedStore?.id, distance: 50000, limit: 5, source: productSource } },
  ])

  const isPickupReady = selectedStore?.isPickupReady
  const isDeliveryReady = selectedStore?.isDeliveryReady

  const showStore =
    productSource === ProductSourceOptions.CLICK_AND_COLLECT
      ? isPickupReady
      : productSource === ProductSourceOptions.DELIVERY
      ? isDeliveryReady
      : true

  const label =
    productSource === ProductSourceOptions.CLICK_AND_COLLECT
      ? 'Available for pickup at'
      : productSource === ProductSourceOptions.DELIVERY
      ? 'Available for delivery at'
      : 'Stocked at'

  const description =
    productSource === ProductSourceOptions.CLICK_AND_COLLECT
      ? `${showStore ? 'Also available' : 'Available'} for pickup at nearby bottleshops:`
      : productSource === ProductSourceOptions.DELIVERY
      ? `${showStore ? 'Also delivered' : 'Delivered'} from nearby bottleshops:`
      : `${showStore ? 'Also stocked' : 'Stocked'} in nearby bottleshops:`

  return (
    <Stack spacing={4}>
      {showStore && (
        <Stack spacing={2}>
          <Flex alignItems={"center"} flexDirection={['column', 'row', 'row']}>
            <Text fontFamily="heading" fontSize="lg">
              {product.currentAvailability || product.specialPricing
                ? label
                : 'Contact store for availability'}
            </Text>
            <Link
              href="/stores/[slug]"
              hrefAs={`/stores/${selectedStore?.slug}`}
              ml={2}
              display="flex"
              fontSize="lg"
              fontFamily="heading"
              alignItems="center"
              color="pink.500"
            >
              {selectedStore?.name?.toUpperCase()}
              <Icon ml={2} name="info-outline" />
            </Link>
          </Flex>

          <Text
            fontSize={['xs', 'sm']}
            fontWeight="bold"
            fontFamily="heading"
            color="blackAlpha.700"
          >
            {selectedStore?.hours && getStoreOpenCloseStatus(selectedStore.hours)}
          </Text>
        </Stack>
      )}

      {data && data.length > 0 && <Text>{description}</Text>}

      {data && data.length > 0 && (
        <Stack>
          {isFetching
            ? range(6).map(() => <Skeleton h="91px" w="100%" />)
            : data.map(store => (
                <StoreCardSlim
                  store={store}
                  icon="chevron-right"
                  onClick={() => {
                    selectStore(store)
                    fetchProductLists(store?.region)
                  }}
                />
              ))}
        </Stack>
      )}

      {data && data.length === 0 && !isFetching && (
        <Text>No stores found nearby with this product.</Text>
      )}
    </Stack>
  )
}

export default ProductAvailabilityStore
